import { useCallback, useState } from 'react'

import { AsyncSelect, Space, SubText, Text } from '@procurify/ui'

import { UserAvatar } from '@webapp/components/UserAvatar'
import { useProcurifyIntl } from '@webapp/hooks'
import { userRepository } from '@webapp/platform/iam/data-access/repositories/UserRepository'
import { Permissions } from '@webapp/platform/iam/entities/enums/PermissionEnum'
import { User } from '@webapp/platform/iam/entities/User'
import messages from './messages'

// Server-side picker filter: only users holding at least one of the four
// report sub-permissions appear (BHV-015). OR-joined into the `permission`
// query param.
const REPORT_ACCESS_USER_FILTER_PERMISSIONS = [
  Permissions.REPORTS_VIEW_EXPENSES_BY_DEPARTMENT,
  Permissions.REPORTS_VIEW_ORDERS_BY_DEPARTMENT,
  Permissions.REPORTS_VIEW_ORDERS_BY_USER,
  Permissions.REPORTS_VIEW_ORDERS_BY_VENDOR,
] as const

export interface IReportAccessUserSelectProps {
  /** Id of the search input, for a label or to move focus to the picker */
  inputId?: string
  value: User | null
  onChange: (user: User | null) => void
  isDisabled?: boolean
}

const formatUserOptionLabel = (
  option: User,
  { context }: { context: 'menu' | 'value' }
) =>
  context === 'menu' ? (
    <Space direction='horizontal' justify='space-between' align='center'>
      <Space direction='horizontal' size='small' align='center'>
        <UserAvatar
          size='small'
          name={option.fullName}
          url={option.profileImage || undefined}
        />
        <Text inline bold>
          {option.fullName}
        </Text>
      </Space>
      <SubText inline color='textTertiary'>
        {option.email}
      </SubText>
    </Space>
  ) : (
    option.fullName
  )

/**
 * Self-contained AsyncSelect over `/api/v3/users/` (ADR-3). The basic user
 * endpoint used by the shared UserSelect does not expose `role`, which this
 * picker needs to exclude Superusers.
 */
export const ReportAccessUserSelect = ({
  inputId,
  value,
  onChange,
  isDisabled = false,
}: IReportAccessUserSelectProps) => {
  const intl = useProcurifyIntl()
  // The search term whose first page failed, so the menu can tell a failed
  // load apart from a genuine no-match
  const [failedSearch, setFailedSearch] = useState<string | undefined>(
    undefined
  )

  // Stable reference so AsyncSelect doesn't reset its pages on re-render
  // (ADR-3); every capture is a module constant, the repository singleton or
  // a state setter
  const loadOptions = useCallback(
    (
      inputValue: string,
      pagination: { page: number },
      callback: (args: {
        options: User[] | null
        pagination: { hasMore: boolean; page: number }
      }) => void
    ) => {
      userRepository
        .list(true, {
          search: inputValue || undefined,
          page: pagination.page,
          orderBy: 'firstName',
          permission: REPORT_ACCESS_USER_FILTER_PERMISSIONS.join('|'),
        })
        .then((res) => {
          setFailedSearch((failed) =>
            failed === inputValue ? undefined : failed
          )
          callback({
            options: res.data
              .map((user) => new User(user))
              // Superusers are excluded client-side (BHV-015)
              .filter((user) => !user.role?.isSuperuser),
            pagination: {
              hasMore: res.pagination.page < res.pagination.numPages,
              page: pagination.page + 1,
            },
          })
        })
        .catch(() => {
          if (pagination.page === 1) setFailedSearch(inputValue)
          // `null` options end the loading state without caching the term as
          // loaded, so typing it again re-requests it
          callback({
            options: null,
            pagination: { hasMore: false, page: pagination.page },
          })
        })
    },
    []
  )

  const noOptionsMessage = useCallback(
    ({ inputValue }: { inputValue: string }) =>
      intl.formatMessage(
        inputValue === failedSearch
          ? messages.loadErrorMessage
          : messages.noOptionsMessage
      ),
    [intl, failedSearch]
  )

  return (
    <AsyncSelect<User>
      cacheOptions
      inputId={inputId}
      loadOptions={loadOptions}
      aria-label={intl.formatMessage(messages.ariaLabel)}
      placeholder={intl.formatMessage(messages.placeholder)}
      noOptionsMessage={noOptionsMessage}
      value={value}
      onChange={(option) => onChange(option ?? null)}
      getOptionValue={(option) => option.id.toString()}
      getOptionLabel={(option) => option.fullName}
      formatOptionLabel={formatUserOptionLabel}
      isClearable
      isDisabled={isDisabled}
    />
  )
}

ReportAccessUserSelect.displayName = 'ReportAccessUserSelect'
